import { makeApi, Zodios, type ZodiosInstance, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const ChallengeStatusEnum = z.enum([
  'ongoing',
  'unbooted',
  'finished',
  'aborted',
])
const queryFilters = z
  .object({
    status: ChallengeStatusEnum,
    userId: z.string(),
    taskId: z.string(),
    assignmentIds: z.array(z.string()),
  })
  .partial()
  .passthrough()
  .optional()
const FeedbackStatusEnum = z.enum([
  'unprocessed',
  'processing1',
  'processing2',
  'processing3',
  'processing4',
  'processing5',
  'processing6',
  'processing7',
  'processing8',
  'processing9',
  'processing10',
  'processing11',
  'processing12',
  'processing13',
  'processing14',
  'processing15',
  'processed',
])
const WorkspaceTabTypeEnum = z.enum([
  'assignment',
  'sql',
  'python',
  'solution',
  'notes',
  'sqlCheatSheet',
  'tipsHistory',
])
const WorkspaceTab = z
  .object({ type: WorkspaceTabTypeEnum, data: z.string().optional() })
  .passthrough()
const WorkspaceTabView = z
  .object({
    activeTab: z.number(),
    tabs: z.array(WorkspaceTab),
    width: z.number().optional(),
  })
  .passthrough()
const WorkState = z
  .object({
    solution: z.string(),
    lastQuery: z.string(),
    notes: z.string(),
    charts: z.record(
      z
        .object({
          id: z.string().optional(),
          chartType: z.enum(['table', 'line', 'bar', 'pie', 'scatter']),
          xAxis: z.string().optional(),
          yAxis: z.string().optional(),
          query: z.string(),
          data: z.array(z.object({}).partial().passthrough()).optional(),
          showTrendLine: z.boolean().optional(),
        })
        .passthrough()
    ),
    tabViews: z.array(WorkspaceTabView),
    jNotebook: z.object({}).partial().passthrough(),
    chosenWeapon: z.enum(['SQL', 'Python']),
    savedAt: z.string().datetime({ offset: true }),
  })
  .partial()
  .passthrough()
const EmployerFeedback = z
  .object({ feedback: z.string(), grade: z.number() })
  .passthrough()
const UserFeedback = z
  .object({
    overallRating: z.number(),
    practiceNextRating: z.number(),
    agreeWithFeedback: z.boolean(),
    whyNoAgree: z.string().optional(),
    freeText: z.string().optional(),
    submissionDate: z.number().optional(),
  })
  .passthrough()
const ChallengeResubmission = z
  .object({
    at: z.string().datetime({ offset: true }),
    userId: z.string(),
    firstName: z.string(),
  })
  .passthrough()
const QueryHistoryEntry = z
  .object({
    query: z.string(),
    resultCount: z.number().optional(),
    errorCode: z.string().optional(),
  })
  .passthrough()
const FeedbackLanguageEnum = z.enum(['en', 'he', 'es', 'fr', 'de'])
const Challenge = z
  .object({
    id: z.string(),
    taskId: z.string(),
    userId: z.string(),
    startTime: z.string().datetime({ offset: true }),
    submittableUntil: z.string().datetime({ offset: true }).optional(),
    workTime: z.number(),
    endTime: z.string().datetime({ offset: true }).optional(),
    lastWorkTimeUpdate: z.string().datetime({ offset: true }).optional(),
    status: ChallengeStatusEnum,
    feedbackStatus: FeedbackStatusEnum.optional(),
    workState: WorkState.optional(),
    feedbackText: z.string().optional(),
    feedbackTips: z.array(z.string()).max(3).optional(),
    feedbackExcerpt: z.string().optional(),
    feedbackGrade: z.number().optional(),
    employerFeedback: EmployerFeedback.optional(),
    slug: z.string(),
    userFeedback: UserFeedback.optional(),
    deleted: z.boolean().optional().default(false),
    assignmentId: z.string().optional(),
    organizationId: z.string().optional(),
    challengeDisclaimer: z.string().optional(),
    submitDisclaimer: z.string().optional(),
    resubmissions: z.array(ChallengeResubmission).optional(),
    sharedDBName: z.string().optional(),
    customQueryTimeout: z.number().gte(1).lte(255).optional(),
    queryHistory: z.array(QueryHistoryEntry).optional(),
    feedbackReleaseOverride: z.boolean().optional(),
    isSandbox: z.boolean().optional(),
    feedbackLanguage: FeedbackLanguageEnum.optional(),
  })
  .passthrough()
const chosenWeapon = z.enum(['SQL', 'Python'])
const ChallengeDifficultyEnum = z.enum([
  'Easy',
  'Intermediate',
  'Hard',
  'Expert',
])
const TaskTableQueriesExtension = z
  .object({ tableQueries: z.array(z.string()) })
  .partial()
  .passthrough()
const Task = z
  .object({
    name: z.string().min(3).max(200),
    body: z.string().min(10).max(50000),
    teaser: z.string().min(10).max(200),
    ownerId: z
      .string()
      .min(1)
      .max(50)
      .regex(/^[a-zA-Z0-9_-]+$/)
      .optional(),
    allowedWeapons: z.array(chosenWeapon).min(1).max(2).optional(),
    difficulty: ChallengeDifficultyEnum,
    description: z.string().min(20).max(10000),
    defaultQuery: z.string().min(1).max(2000),
    seedFactor: z.number().optional(),
    feedbackPayload: z.string().min(2).optional(),
    dataBank: z.string().min(2),
    accessGroups: z
      .array(
        z
          .string()
          .min(1)
          .max(50)
          .regex(/^[a-zA-Z0-9_-]+$/)
      )
      .min(1)
      .max(10),
    proficiencies: z
      .array(
        z.enum([
          'joins',
          'dataSanitization',
          'anomalyDetection',
          'timeSeriesAnalysis',
          'windowFunctions',
          'aggregation',
        ])
      )
      .optional(),
    solutionPlaceholder: z.string().optional(),
    availabilityWeight: z.number().optional(),
    isSandboxTask: z.boolean().optional(),
    deleted: z.boolean().optional(),
    sharedDBName: z.string().optional(),
    customQueryTimeout: z.number().gte(1).lte(255).optional(),
    slug: z.string().optional(),
    coverImageUrl: z.string().optional(),
    coverFocusY: z.number().gte(0).lte(100).optional(),
  })
  .passthrough()
  .and(z.object({ id: z.string() }).passthrough())
  .and(TaskTableQueriesExtension)
const ChallengeInfo = z
  .object({
    challenge: Challenge,
    task: Task,
    user: z
      .object({
        userId: z.string(),
        fullName: z.string(),
        role: z.enum(['admin', 'user', 'partner']),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()
const ChallengeErrorCode = z.enum([
  'challengeNotFound',
  'badRequest',
  'invalidChallengeId',
  'challengeNotOngoing',
  'challengeInCooldown',
  'databaseCreationFailed',
  'databasePopulationFailed',
  'databaseConnectionFailed',
  'taskNotFound',
  'unauthorized',
  'loggedOut',
  'unknownError',
  'invalidAssignment',
  'queryTimeout',
  'disallowedPatterns',
  'timeWindowEnded',
  'sandboxNotSubmittable',
])
const ErrorResponse = z
  .object({ error: z.string(), errorCode: ChallengeErrorCode.optional() })
  .passthrough()
const FeedbackStatusFilterOption = z.enum([
  'no-feedback',
  'unprocessed',
  'processing',
  'processed',
])
const ChallengeListFilter = z
  .object({
    status: z.array(ChallengeStatusEnum),
    feedbackStatus: z.array(FeedbackStatusFilterOption),
    userId: z.array(z.string()),
    taskId: z.array(z.string()),
    assignmentIds: z.array(z.string()),
    slug: z.string(),
    weaponOfChoice: z.array(chosenWeapon),
    includeDeleted: z.boolean(),
  })
  .partial()
  .passthrough()
const ChallengeSortFieldEnum = z.enum([
  'id',
  'userId',
  'taskId',
  'status',
  'feedbackStatus',
  'workTime',
  'feedbackGrade',
  'startTime',
  'endTime',
  'lastWorkTimeUpdate',
  'feedbackReleaseOverride',
  'slug',
])
const SortDirectionEnum = z.enum(['asc', 'desc'])
const ListChallengesRequest = z
  .object({
    filter: ChallengeListFilter,
    sortField: ChallengeSortFieldEnum,
    sortDirection: SortDirectionEnum,
    limit: z.number().int().gte(1),
    offset: z.number().int().gte(0),
  })
  .partial()
  .passthrough()
const CountResponse = z.object({ count: z.number() }).passthrough()
const AvailableChallengeStatusEnum = z.enum([
  'available',
  'unbooted',
  'ongoing',
  'cooldown',
  'aborted',
  'finished',
])
const TimeCategoryEnum = z.enum(['New', 'upTo30', 'upTo60', '60OrMore'])
const AvailableChallenge = z
  .object({
    challengeId: z.string().optional(),
    slug: z.string(),
    taskId: z.string(),
    taskSlug: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
    allowedWeapons: z.array(chosenWeapon).min(1).max(2),
    difficulty: ChallengeDifficultyEnum,
    status: AvailableChallengeStatusEnum,
    cooldownRemainingTime: z.number().optional(),
    teaser: z.string(),
    proficiencies: z.array(z.string()).optional(),
    assignmentId: z.string().optional(),
    timeCategory: TimeCategoryEnum,
    workTime: z.number().optional(),
    coverImageUrl: z.string().optional(),
    coverFocusY: z.number().optional(),
    weaponOfChoice: chosenWeapon.optional(),
    isSandbox: z.boolean().optional(),
  })
  .passthrough()
const AvailableAssignment = z
  .object({
    challengeId: z.string().optional(),
    slug: z.string().optional(),
    taskId: z.string(),
    taskSlug: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
    allowedWeapons: z.array(chosenWeapon).min(1).max(2),
    status: AvailableChallengeStatusEnum,
    teaser: z.string().optional(),
    organizationId: z.string(),
    organizationName: z.string(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    overdueDate: z.string().optional(),
    timeLimit: z.number().optional(),
    assignmentId: z.string(),
    workTime: z.number().optional(),
    coverImageUrl: z.string().optional(),
    challengeDisclaimer: z.string().optional(),
    submitDisclaimer: z.string().optional(),
  })
  .passthrough()
const ChallengesResponse = z
  .object({
    count: z.number(),
    availableChallenges: z.array(AvailableChallenge),
    availableAssignments: z.array(AvailableAssignment),
    hasFinishedAssignment: z.boolean().optional(),
  })
  .passthrough()
const DeleteChallengeResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const SyncItem = z
  .object({
    t: z.number(),
    k: z.number().int(),
    s: z.number().int().optional(),
    n: z.number().int().optional(),
    l: z.number().int().optional(),
    r: z.union([z.literal(0), z.literal(1)]).optional(),
    i: z.union([z.literal(0), z.literal(1)]).optional(),
    p: z.string().max(200).optional(),
    q: z.number().int().optional(),
    a: z.number().int().optional(),
    d: z.number().int().optional(),
    e: z.string().max(100).optional(),
  })
  .passthrough()
const SyncChallengeRequest = z
  .object({ b: z.array(SyncItem).max(500) })
  .passthrough()
const SaveWorkResponse = z
  .object({ success: z.boolean() })
  .partial()
  .passthrough()
const ActivityBandEnum = z.enum(['high', 'medium', 'low'])
const ActivityReportSummary = z
  .object({
    activeMinutes: z.number().int(),
    idleMinutes: z.number().int(),
    longestAwayMs: z.number().int(),
    externalInserts: z.number().int(),
    internalInserts: z.number().int(),
    pastedChars: z.number().int(),
    typedChars: z.number().int(),
    pastedShare: z.number(),
    saves: z.number().int(),
    runs: z.number().int(),
    runErrors: z.number().int(),
    clientTelemetryReceived: z.boolean(),
  })
  .passthrough()
const ActivitySeverityEnum = z.enum([
  'info',
  'notable',
  'suspicious',
  'highly_suspicious',
])
const ActivityReportReason = z
  .object({
    rule: z.string(),
    label: ActivitySeverityEnum,
    penalty: z.number(),
    count: z.number().int(),
  })
  .passthrough()
const ActivityReportEvent = z
  .object({
    t: z.number(),
    type: z.enum([
      'paste',
      'drop',
      'inject',
      'active',
      'blur',
      'focus',
      'multi_tab',
      'run',
      'save',
      'submit',
    ]),
    surface: z.enum(['sql', 'notebook', 'solution', 'notes']).optional(),
    len: z.number().int().optional(),
    lines: z.number().int().optional(),
    replacedAll: z.boolean().optional(),
    internal: z.boolean().optional(),
    preview: z.string().optional(),
    pauseMs: z.number().int().optional(),
    awayMs: z.number().int().optional(),
    durationMs: z.number().int().optional(),
    error: z.string().optional(),
    label: ActivitySeverityEnum,
    rule: z.string().optional(),
  })
  .passthrough()
const ActivityReport = z
  .object({
    score: z.number(),
    band: ActivityBandEnum,
    startedAt: z.string(),
    endedAt: z.string().nullish(),
    summary: ActivityReportSummary,
    reasons: z.array(ActivityReportReason),
    events: z.array(ActivityReportEvent),
  })
  .passthrough()
const BootEnvironmentRequest = z
  .object({
    taskId: z.string(),
    assignmentId: z.string().optional(),
    chosenWeapon: chosenWeapon.optional(),
  })
  .passthrough()
const BootEnvironmentResponse = z
  .object({ success: z.boolean(), slug: z.string() })
  .passthrough()
const RunQueryRequest = z
  .object({ challengeId: z.string(), query: z.string() })
  .passthrough()
const RunQueryResponse = z
  .object({
    results: z.array(z.record(z.union([z.string(), z.number()]))),
    error: z.string(),
    hint: z.string(),
  })
  .partial()
  .passthrough()
const SaveWorkRequest = z
  .object({ challengeId: z.string(), workState: WorkState.optional() })
  .passthrough()
const SubmitSolutionRequest = z
  .object({ challengeId: z.string() })
  .passthrough()
const SubmitSolutionResponse = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
    submitDisclaimer: z.string().optional(),
  })
  .passthrough()
const SubmitUserFeedbackRequest = z
  .object({ challengeId: z.string(), userFeedback: UserFeedback })
  .passthrough()
const SubmitUserFeedbackResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const ReportWorkTimeRequest = z
  .object({ challengeId: z.string() })
  .passthrough()
const ReportWorkTimeResponse = z.object({ workTime: z.number() }).passthrough()
const RollChallengeResponse = z
  .object({
    taskId: z.string(),
    allowedWeapons: z.array(chosenWeapon).min(1).max(2),
  })
  .passthrough()
const UnsubmitChallengeRequest = z
  .object({ challengeId: z.string() })
  .passthrough()
const UnsubmitChallengeResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const FetchTableDataRequest = z
  .object({
    challengeId: z.string(),
    tableName: z.string(),
    offset: z.number().int().gte(0).optional(),
    limit: z.number().int().gte(1).optional(),
  })
  .passthrough()
const FetchTableDataResponse = z
  .object({
    results: z.array(z.record(z.union([z.string(), z.number()]))),
    totalRows: z.number().int(),
    hasMore: z.boolean(),
    error: z.string(),
  })
  .partial()
  .passthrough()
const TableMetadataRequest = z
  .object({ challengeId: z.string() })
  .passthrough()
const TableMetadataEntry = z
  .object({
    name: z.string(),
    rowCount: z.number().int(),
    sizeBytes: z.number().int(),
  })
  .passthrough()
const TableMetadataResponse = z
  .object({ tables: z.array(TableMetadataEntry) })
  .passthrough()
const ReleaseFeedbackRequest = z
  .object({ challengeId: z.string(), release: z.boolean() })
  .passthrough()
const ReleaseFeedbackResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const ResubmitChallengeRequest = z
  .object({ challengeId: z.string() })
  .passthrough()
const ResubmitChallengeResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const ChallengeTipsEntry = z
  .object({
    challengeId: z.string(),
    taskId: z.string(),
    tips: z.array(z.string()),
    date: z.string().datetime({ offset: true }),
  })
  .passthrough()
const GradeHistoryEntry = z
  .object({
    challengeId: z.string(),
    taskId: z.string(),
    grade: z.number(),
    date: z.string().datetime({ offset: true }),
  })
  .passthrough()
const UserProgress = z
  .object({
    userId: z.string(),
    tips: z.array(ChallengeTipsEntry).optional(),
    gradeHistory: z.array(GradeHistoryEntry).optional(),
    totalChallengesCompleted: z.number().int().optional(),
    totalWorkTime: z.number().optional(),
    lastActivityDate: z.string().datetime({ offset: true }).optional(),
  })
  .passthrough()
const RecentTipsEntry = z
  .object({
    challengeId: z.string(),
    taskId: z.string(),
    taskName: z.string(),
    tips: z.array(z.string()),
    date: z.string().datetime({ offset: true }),
  })
  .passthrough()
const RecentTipsResponse = z
  .object({ tips: z.array(RecentTipsEntry) })
  .passthrough()
const RecalculateProgressRequest = z
  .object({ userId: z.string() })
  .passthrough()
const RecalculateProgressResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const AdminActiveUsersSeriesRequest = z
  .object({ excludeUserIds: z.array(z.string()) })
  .passthrough()
const ActiveUsersPoint = z
  .object({
    date: z.string(),
    dau: z.number(),
    wau: z.number(),
    mau: z.number(),
  })
  .passthrough()
const AdminActiveUsersSeriesResponse = z
  .object({ series: z.array(ActiveUsersPoint) })
  .passthrough()
const AdminBackfillUserActiveDaysRequest = z
  .object({ trial: z.boolean() })
  .partial()
  .passthrough()
const AdminBackfillUserActiveDaysResponse = z
  .object({
    success: z.boolean(),
    trial: z.boolean(),
    stats: z
      .object({
        challengesScanned: z.number(),
        distinctUserDays: z.number(),
        inserted: z.number(),
        firstDay: z.string().optional(),
        lastDay: z.string().optional(),
      })
      .passthrough(),
  })
  .passthrough()
const AdminDeleteUserChallengesRequest = z
  .object({ userId: z.string(), dryRun: z.boolean() })
  .passthrough()
const AdminDeleteUserChallengesResponse = z
  .object({
    challenges: z.array(z.object({}).partial().passthrough()),
    deletedCount: z.number(),
    droppedDatabases: z.number(),
  })
  .passthrough()
const AdminMigrateTaskRequest = z
  .object({ taskId: z.string(), sharedDBName: z.string(), userId: z.string() })
  .passthrough()
const AdminMigrateTaskResponse = z
  .object({
    success: z.boolean(),
    challengeId: z.string().optional(),
    steps: z.array(
      z
        .object({
          step: z.string(),
          status: z.string(),
          error: z.string().optional(),
        })
        .passthrough()
    ),
  })
  .passthrough()
const AchievementOrganization = z
  .object({ id: z.string(), name: z.string(), logoURL: z.string().nullish() })
  .passthrough()
const PublicAchievementPayload = z
  .object({
    status: z.enum(['ok', 'hidden', 'not_found']),
    slug: z.string().optional(),
    name: z.string().optional(),
    taskName: z.string().optional(),
    attemptNumber: z.number().int().optional(),
    grade: z.number().optional(),
    submittedAt: z.string().datetime({ offset: true }).optional(),
    percentile: z.number().int().nullish(),
    percentileAvailable: z.boolean().optional(),
    workTime: z.number().nullish(),
    memberSince: z.string().datetime({ offset: true }).nullish(),
    feedbackExcerpt: z.string().nullish(),
    weaponOfChoice: chosenWeapon.nullish(),
    organization: AchievementOrganization.nullish(),
  })
  .passthrough()
const ViewBody = z
  .object({ referrer: z.string().nullable() })
  .partial()
  .passthrough()
const Achievement = z
  .object({
    persisted: z.boolean(),
    slug: z.string(),
    isPublic: z.boolean(),
    showPercentile: z.boolean(),
    showWorkTime: z.boolean(),
    showMemberSince: z.boolean(),
    showFeedbackExcerpt: z.boolean(),
    firstPublishedAt: z.string().datetime({ offset: true }).nullable(),
    name: z.string(),
    taskName: z.string(),
    attemptNumber: z.number().int(),
    grade: z.number(),
    submittedAt: z.string().datetime({ offset: true }),
    percentile: z.number().int().nullable(),
    percentileAvailable: z.boolean(),
    workTime: z.number().nullable(),
    memberSince: z.string().datetime({ offset: true }).nullable(),
    feedbackExcerpt: z.string().nullable(),
    weaponOfChoice: chosenWeapon.nullable(),
    organization: AchievementOrganization.nullable(),
  })
  .passthrough()
const AchievementSettingsPatch = z
  .object({
    isPublic: z.boolean(),
    showPercentile: z.boolean(),
    showWorkTime: z.boolean(),
    showMemberSince: z.boolean(),
    showFeedbackExcerpt: z.boolean(),
  })
  .partial()
  .passthrough()
const AchievementAnalytics = z
  .object({
    totalViews: z.number().int(),
    last7Days: z.number().int(),
    dailySeries: z.array(
      z.object({ _id: z.string(), count: z.number().int() }).passthrough()
    ),
    topSources: z.array(
      z.object({ _id: z.string(), count: z.number().int() }).passthrough()
    ),
  })
  .passthrough()

export const schemas = {
  ChallengeStatusEnum,
  queryFilters,
  FeedbackStatusEnum,
  WorkspaceTabTypeEnum,
  WorkspaceTab,
  WorkspaceTabView,
  WorkState,
  EmployerFeedback,
  UserFeedback,
  ChallengeResubmission,
  QueryHistoryEntry,
  FeedbackLanguageEnum,
  Challenge,
  chosenWeapon,
  ChallengeDifficultyEnum,
  TaskTableQueriesExtension,
  Task,
  ChallengeInfo,
  ChallengeErrorCode,
  ErrorResponse,
  FeedbackStatusFilterOption,
  ChallengeListFilter,
  ChallengeSortFieldEnum,
  SortDirectionEnum,
  ListChallengesRequest,
  CountResponse,
  AvailableChallengeStatusEnum,
  TimeCategoryEnum,
  AvailableChallenge,
  AvailableAssignment,
  ChallengesResponse,
  DeleteChallengeResponse,
  SyncItem,
  SyncChallengeRequest,
  SaveWorkResponse,
  ActivityBandEnum,
  ActivityReportSummary,
  ActivitySeverityEnum,
  ActivityReportReason,
  ActivityReportEvent,
  ActivityReport,
  BootEnvironmentRequest,
  BootEnvironmentResponse,
  RunQueryRequest,
  RunQueryResponse,
  SaveWorkRequest,
  SubmitSolutionRequest,
  SubmitSolutionResponse,
  SubmitUserFeedbackRequest,
  SubmitUserFeedbackResponse,
  ReportWorkTimeRequest,
  ReportWorkTimeResponse,
  RollChallengeResponse,
  UnsubmitChallengeRequest,
  UnsubmitChallengeResponse,
  FetchTableDataRequest,
  FetchTableDataResponse,
  TableMetadataRequest,
  TableMetadataEntry,
  TableMetadataResponse,
  ReleaseFeedbackRequest,
  ReleaseFeedbackResponse,
  ResubmitChallengeRequest,
  ResubmitChallengeResponse,
  ChallengeTipsEntry,
  GradeHistoryEntry,
  UserProgress,
  RecentTipsEntry,
  RecentTipsResponse,
  RecalculateProgressRequest,
  RecalculateProgressResponse,
  AdminActiveUsersSeriesRequest,
  ActiveUsersPoint,
  AdminActiveUsersSeriesResponse,
  AdminBackfillUserActiveDaysRequest,
  AdminBackfillUserActiveDaysResponse,
  AdminDeleteUserChallengesRequest,
  AdminDeleteUserChallengesResponse,
  AdminMigrateTaskRequest,
  AdminMigrateTaskResponse,
  AchievementOrganization,
  PublicAchievementPayload,
  ViewBody,
  Achievement,
  AchievementSettingsPatch,
  AchievementAnalytics,
}

const endpoints: ReturnType<typeof makeApi> = makeApi([
  {
    method: 'get',
    path: '/',
    alias: 'getAllChallenges',
    description: `Retrieves all challenges with optional filtering, sorting and pagination`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'queryFilters',
        type: 'Query',
        schema: queryFilters,
      },
      {
        name: 'sortField',
        type: 'Query',
        schema: z
          .enum([
            'id',
            'userId',
            'taskId',
            'status',
            'feedbackStatus',
            'workTime',
            'feedbackGrade',
            'startTime',
            'endTime',
            'lastWorkTimeUpdate',
            'feedbackReleaseOverride',
            'slug',
          ])
          .optional(),
      },
      {
        name: 'sortDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().gte(0).optional(),
      },
    ],
    response: z.array(ChallengeInfo),
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'delete',
    path: '/:id',
    alias: 'deleteChallenge',
    description: `Soft-deletes a challenge by setting the deleted flag to true. Admin only.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: DeleteChallengeResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/:id/activity-report',
    alias: 'getActivityReport',
    description: `Reliability score, aggregates and labelled events for a challenge. Admin only.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: ActivityReport,
    errors: [
      {
        status: 403,
        description: `Forbidden`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/:id/sync',
    alias: 'syncChallenge',
    description: `Sync batch`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SyncChallengeRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }).partial().passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/:slug',
    alias: 'getChallengeBySlug',
    description: `Retrieves a specific challenge by its slug`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'slug',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: ChallengeInfo,
    errors: [
      {
        status: 404,
        description: `Route not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/achievement/by-challenge/:challengeSlug',
    alias: 'getAchievementByChallenge',
    requestFormat: 'json',
    parameters: [
      {
        name: 'challengeSlug',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Achievement,
  },
  {
    method: 'put',
    path: '/achievement/by-challenge/:challengeSlug',
    alias: 'updateAchievementByChallenge',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AchievementSettingsPatch,
      },
      {
        name: 'challengeSlug',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Achievement,
  },
  {
    method: 'get',
    path: '/achievement/by-challenge/:challengeSlug/analytics',
    alias: 'getAchievementAnalytics',
    requestFormat: 'json',
    parameters: [
      {
        name: 'challengeSlug',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AchievementAnalytics,
  },
  {
    method: 'post',
    path: '/admin/active-users-series',
    alias: 'adminGetActiveUsersSeries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AdminActiveUsersSeriesRequest,
      },
    ],
    response: AdminActiveUsersSeriesResponse,
  },
  {
    method: 'post',
    path: '/admin/delete-user-challenges',
    alias: 'adminDeleteUserChallenges',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AdminDeleteUserChallengesRequest,
      },
    ],
    response: AdminDeleteUserChallengesResponse,
  },
  {
    method: 'post',
    path: '/admin/migrate-task-to-shared-db',
    alias: 'adminMigrateTaskToSharedDB',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AdminMigrateTaskRequest,
      },
    ],
    response: AdminMigrateTaskResponse,
  },
  {
    method: 'post',
    path: '/admin/migrations/backfill-user-active-days',
    alias: 'adminBackfillUserActiveDays',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ trial: z.boolean() }).partial().passthrough(),
      },
    ],
    response: AdminBackfillUserActiveDaysResponse,
  },
  {
    method: 'get',
    path: '/available-challenges',
    alias: 'getAvailableChallenges',
    description: `Retrieves a list of all available challenges`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'accessGroups',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: ChallengesResponse,
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/boot',
    alias: 'bootEnvironment',
    description: `Boots the environment for a specific challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BootEnvironmentRequest,
      },
    ],
    response: BootEnvironmentResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/count',
    alias: 'getChallengeCount',
    description: `Retrieves the count of challenges filtered by status`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'status',
        type: 'Query',
        schema: z.enum(['ongoing', 'unbooted', 'finished', 'aborted']),
      },
    ],
    response: z.object({ count: z.number() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request - status parameter required`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/feedback-pdf',
    alias: 'getFeedbackPdf',
    description: `Generates and returns a PDF document with the candidate&#x27;s feedback for a given challenge. Requires partner access; verifies the challenge&#x27;s assignment belongs to the partner&#x27;s organization.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'challengeId',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad request - missing challengeId or challenge has no feedback`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden - challenge does not belong to partner&#x27;s organization`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/finished-challenges',
    alias: 'getFinishedChallenges',
    description: `Retrieves all finished challenges for the current user`,
    requestFormat: 'json',
    response: z.array(ChallengeInfo),
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/list',
    alias: 'listChallenges',
    description: `Retrieves challenges with a structured filter object plus pagination and sort. Admin only.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListChallengesRequest,
      },
    ],
    response: z.array(ChallengeInfo),
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/ongoing-challenges',
    alias: 'getOngoingChallenges',
    description: `Retrieves all ongoing challenges for the logged-in user with their task details`,
    requestFormat: 'json',
    response: ChallengesResponse,
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/query',
    alias: 'runQuery',
    description: `Executes a query for a specific challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RunQueryRequest,
      },
    ],
    response: RunQueryResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid parameters`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/recalculate-progress',
    alias: 'recalculateProgress',
    description: `Admin endpoint to recalculate a user&#x27;s progress record from their challenges`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ userId: z.string() }).passthrough(),
      },
    ],
    response: RecalculateProgressResponse,
    errors: [
      {
        status: 404,
        description: `User not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/release-feedback',
    alias: 'releaseFeedback',
    description: `Toggles manual feedback release override for a specific challenge. Admin only.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReleaseFeedbackRequest,
      },
    ],
    response: ReleaseFeedbackResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId or payload`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/report-worktime',
    alias: 'reportWorkTime',
    description: `Reports work time for a specific challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ challengeId: z.string() }).passthrough(),
      },
    ],
    response: z.object({ workTime: z.number() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Route not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/resubmit-challenge',
    alias: 'resubmitChallenge',
    description: `Admin action: resets a finished challenge back to ongoing, clears endTime, and re-submits the solution to the feedback service. Useful when feedback processing failed or needs to be re-triggered.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ challengeId: z.string() }).passthrough(),
      },
    ],
    response: ResubmitChallengeResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId or challenge cannot be resubmitted`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/roll-challenge',
    alias: 'rollChallenge',
    description: `Returns a random task ID from existing available tasks`,
    requestFormat: 'json',
    response: RollChallengeResponse,
    errors: [
      {
        status: 404,
        description: `No available tasks found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/save-work',
    alias: 'saveWork',
    description: `Saves the current work progress for a challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SaveWorkRequest,
      },
    ],
    response: z.object({ success: z.boolean() }).partial().passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request - invalid parameters`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/submission/by-slug/:slug',
    alias: 'getPublicAchievement',
    requestFormat: 'json',
    parameters: [
      {
        name: 'slug',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: PublicAchievementPayload,
    errors: [
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/submission/by-slug/:slug/view',
    alias: 'recordPublicAchievementView',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({ referrer: z.string().nullable() })
          .partial()
          .passthrough()
          .optional(),
      },
      {
        name: 'slug',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/submit-solution',
    alias: 'submitSolution',
    description: `Submits a solution for a specific challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ challengeId: z.string() }).passthrough(),
      },
    ],
    response: SubmitSolutionResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId or solution`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Route not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/submit-user-feedback',
    alias: 'submitUserFeedback',
    description: `Submits user feedback for a completed challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SubmitUserFeedbackRequest,
      },
    ],
    response: SubmitUserFeedbackResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId or feedback payload`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/table-data',
    alias: 'fetchTableData',
    description: `Returns all rows from a specific table in the challenge&#x27;s database. Validates the table name against the task&#x27;s dataBank keys.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FetchTableDataRequest,
      },
    ],
    response: FetchTableDataResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId or tableName`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge or table not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/table-metadata',
    alias: 'getTableMetadata',
    description: `Returns metadata (name, row count, size) for all tables in the challenge&#x27;s database.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ challengeId: z.string() }).passthrough(),
      },
    ],
    response: TableMetadataResponse,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/unsubmit',
    alias: 'unsubmitChallenge',
    description: `Admin only. Reopens a finished challenge so the user can keep working and submit again: resets status to ongoing, clears end date, feedback data and resubmission history.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ challengeId: z.string() }).passthrough(),
      },
    ],
    response: UnsubmitChallengeResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/user-progress',
    alias: 'getUserProgress',
    description: `Retrieves the accumulated progress record for the current user`,
    requestFormat: 'json',
    response: UserProgress,
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/user-progress/recent-tips',
    alias: 'getRecentTips',
    description: `Retrieves the last 4 tips entries for the current user`,
    requestFormat: 'json',
    response: RecentTipsResponse,
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
])

export const api: ZodiosInstance<typeof endpoints> = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions): ZodiosInstance<typeof endpoints> {
  return new Zodios(baseUrl, endpoints, options)
}
